import React from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { Link } from "gatsby";

import { theme } from "../../../utils/global-styles/theme";

const EnrollmentNotification = ({
  setEnrollmentNotification,
  clearPending,
  notification,
  notificationData,
}) => {
  const { status, type, message } = notification;
  const enrollmentSuccess = status === "success",
    EnrollmentNotificationGrid = styled.div`
      display: grid;
      grid-template-rows: 48px 1fr 250px;
      grid-template-columns: 328px 1fr 328px;
      font-family: ${theme.fontFaces.headers};

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        grid-template-rows: 36px 1fr 50px;
        grid-template-columns: 14px 1fr 14px;
      }
    `,
    NotificationTextContainer = styled.div`
      grid-area: 2 / 2 / span 1 / span 1;
    `,
    NotificationHeader = styled.h2`
      font-size: 53px;
      font-weight: bold;
      letter-spacing: -1.38px;
      line-height: 64px;
      margin-bottom: 28px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 32px;
        letter-spacing: -0.83px;
        line-height: 38px;
        margin-bottom: 9px;
      }
    `,
    NotificationDescription = styled.h3`
      font-size: 30px;
      font-weight: bold;
      letter-spacing: -0.09px;
      line-height: 37px;
      margin-bottom: 49px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 24px;
        letter-spacing: -0.22px;
        line-height: 30px;
        margin-bottom: 32px;
      }
    `,
    NotificationText = styled.h4`
      font-size: 24px;
      font-weight: 500;
      letter-spacing: -0.09px;
      line-height: 30px;
      margin-bottom: 37px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 20px;
        font-weight: 500;
        letter-spacing: -0.18px;
        line-height: 24px;
        margin-bottom: 22px;
      }
    `,
    CtaButton = styled(Link)`
      height: 36px;
      width: 216px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: 0.4px;
      line-height: 15px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 12px;
        font-weight: bold;
        letter-spacing: 0.34px;
        line-height: 15px;
        width: 185px;
      }
    `,
    SSOButton = styled.button`
      height: 36px;
      width: 216px;
      display: flex;
      justify-content: center;
      align-items: center;
      border: none;
      font-size: 14px;
      cursor: pointer;
      font-weight: bold;
      letter-spacing: 0.4px;
      line-height: 15px;

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 12px;
        font-weight: bold;
        letter-spacing: 0.34px;
        line-height: 15px;
        width: 185px;
      }
    `,
    // The Return Button does not follow our primary and secondary color className conventions because global className don't work on it for some reason.
    ReturnButton = styled.div`
      height: 36px;
      width: 216px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: 0.4px;
      line-height: 15px;
      cursor: pointer;
      color: ${theme.colors.white};
      background-color: ${theme.colors.primary};

      :hover {
        background-color: ${theme.colors.secondary};
        transition: 0.2s ease-in-out;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        font-size: 12px;
        font-weight: bold;
        letter-spacing: 0.34px;
        line-height: 15px;
        width: 185px;
      }
    `;

  return (
    <EnrollmentNotificationGrid>
      <NotificationTextContainer>
        {enrollmentSuccess ? (
          <NotificationHeader>{notificationData.title}</NotificationHeader>
        ) : (
          <NotificationHeader>Sorry!</NotificationHeader>
        )}
        {enrollmentSuccess ? (
          <NotificationText>{notificationData.options[0]}</NotificationText>
        ) : type === "course" ? (
          <NotificationText>
           { message || "Something has gone wrong with your enrollment for this course. Please try again. If you continue to see this message, please contact customer support for assistance."}
          </NotificationText>
        ) : (
          <NotificationText>
            Something has gone wrong with your application for this program.
            Please try again. If you continue to see this message, please
            contact customer support for assistance.
          </NotificationText>
        )}
        {enrollmentSuccess ? (
          type === "course" ? (
            <CtaButton
              to={`${notificationData.link.url}/`}
              className="white primary-bg secondary-background-hover"
              onClick={() => clearPending && clearPending()}
              data-testId="enrollment-success-cta-button"
            >
              {notificationData.link.linkText}
            </CtaButton>
          ) : (
            <SSOButton
              className="white primary-bg secondary-background-hover"
              onClick={() => clearPending && clearPending(true)}
              data-testId="sso-button"
            >
              {notificationData.link.linkText}
            </SSOButton>
          )
        ) : setEnrollmentNotification ? (
          <ReturnButton
            className="primary-bg white secondary-background-hover"
            onClick={() => {
              setEnrollmentNotification(null);
              history.back();
            }}
            data-testId="return-button"
          >
            Go Back
          </ReturnButton>
        ) : (
          <CtaButton
            to={`${notificationData.link.url}/`}
            className="white primary-bg secondary-background-hover"
            onClick={() => clearPending && clearPending()}
            data-testId="no-enrollment-notification-cta"
          >
            {notificationData.link.linkText}
          </CtaButton>
        )}
      </NotificationTextContainer>
    </EnrollmentNotificationGrid>
  );
};

EnrollmentNotification.propTypes = {
  setEnrollmentNotification: PropTypes.func,
  clearPending: PropTypes.func,
  notification: PropTypes.shape({
    status: PropTypes.string,
    type: PropTypes.string,
  }),
  enrollmentNotificationData: PropTypes.shape({
    title: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
    link: PropTypes.shape({
      url: PropTypes.string,
      linkText: PropTypes.string,
    }),
  }),
};

export default EnrollmentNotification;
